import { format } from 'date-fns';
import { addMethod, boolean, date, mixed, number, object, ref, string } from 'yup';

import {
  ApplicationMethodTypeId,
  CalculationMethods,
  DiscountAppliesTo,
  ThresholdType,
  TotalItemConditionType,
} from '../../models';

import type { ConfigureStepState } from '../state/configure';
import type { DetailsStepState } from '../state/details';
import type { OnlineDetailsStepState } from '../state/online-details';
import type { RequirementsStepState } from '../state/requirements';
import type { RewardsStepState } from '../state/rewards';
import type { ActiveDaysMap } from 'src/app/components/lib/day-of-week-picker';

/**
 * The default value for number inputs is '' as they are controlled components
 *  and passing undefined for the default is a React error.
 * This transform call prevents the validate function from throwing an error for the '' default value
 */
addMethod(number, 'validEmptyString', function validEmptyString() {
  return this.transform((value) => (Number.isNaN(value) ? 0 : value));
});

const configureStepSchema = object({
  applicationMethod: number().required(),
  discountName: string().required(),
  discountCode: string().when('applicationMethod', {
    is: ApplicationMethodTypeId.ByCode,
    then: (schema) => schema.required(),
  }),
  externalId: string().when('applicationMethod', {
    is: (applicationMethd) =>
      [ApplicationMethodTypeId.ByAlpineIQ, ApplicationMethodTypeId.BySpringBig].includes(applicationMethd),
    then: (schema) => schema.required(),
  }),
  loyaltyPointsToRedeem: mixed().test(
    'Loyalty points',
    'Loyalty points must be greater than zero',
    (value, context) => {
      // field is required if applicationMethod is External Loyalty or Loyalty and value must be a positive number
      const applicationMethodShouldMakeFieldRequired =
        context.parent.applicationMethod === ApplicationMethodTypeId.ExternalLoyalty ||
        context.parent.applicationMethod === ApplicationMethodTypeId.DutchieLoyalty;
      const valid = applicationMethodShouldMakeFieldRequired ? value !== '' : true;
      return valid;
    }
  ),
});

export function isConfigureStepValid(arg: ConfigureStepState) {
  try {
    configureStepSchema.validateSync(arg);
    return true;
  } catch {
    return false;
  }
}

export type IsDetailsStepValidArg = Pick<
  DetailsStepState,
  | 'activeDaysOfTheWeek'
  | 'endDate'
  | 'endTime'
  | 'limitPerCustomer'
  | 'limitPerCustomerQuantity'
  | 'redemptionLimit'
  | 'requireManagerApproval'
  | 'restrictByDayOfWeek'
  | 'specifyEndDate'
  | 'startDate'
  | 'startTime'
>;

const detailStepSchema = object({
  activeDaysOfTheWeek: object().test((value, context) => {
    const valid = context.parent.restrictByDayOfWeek === true ? Object.values(value).some((active) => active) : true;
    return valid;
  }),
  endDate: date()
    .nullable()
    .when('specifyEndDate', {
      is: true,
      then: (schema) => schema.required(),
    }),
  limitPerCustomer: boolean().required(),
  limitPerCustomerQuantity: number()
    .validEmptyString()
    .when('limitPerCustomer', {
      is: true,
      then: (schema) => schema.required().min(1),
    }),
  redemptionLimit: mixed().test(
    'Redemption limit',
    'Redemption limit must be an integer between 1 and 99,999',
    (value) => {
      // field is not required but if a value is provided it must be less than 100,000
      const asNumber = Number(value);
      const valid = value === '' ? true : asNumber < 100000 && asNumber >= 0 && asNumber % 1 === 0;
      return valid;
    }
  ),
  requireManagerApproval: boolean().required(),
  restrictByDayOfWeek: boolean().required(),
  specifyEndDate: boolean().required(),
  startDate: date().nullable().required(),
  startTime: string().test('valid-time-format', 'Time format is invalid', (value) => {
    if (!value || value.trim() === '') {
      return true; // Empty is valid (optional field)
    }
    // Validate format: H:MM or HH:MM with valid hour (1-12) and minutes (0-59)
    const timeRegex = /^([1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/;
    return timeRegex.test(value);
  }),
  endTime: string().test(
    'valid-time-range',
    'End time must be later than start time',
    function validateEndTimeRange(value) {
      const { startTime, activeDaysOfTheWeek } = this.parent;

      // First validate the format of endTime
      if (value && value.trim() !== '') {
        const timeRegex = /^([1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/;
        if (!timeRegex.test(value)) {
          return false; // Invalid format
        }
      }

      // Then validate the time range (need default for activeDaysOfTheWeek)
      const timeRangeError = validateTimeRange(startTime || '', value || '', activeDaysOfTheWeek);
      return timeRangeError === null;
    }
  ),
  availableOnline: boolean(),
});

export function isDetailsStepValid(detailsStep: IsDetailsStepValidArg) {
  try {
    detailStepSchema.validateSync(detailsStep);
    return true;
  } catch {
    return false;
  }
}

export function isAnyDayOfWeekActive(map: ActiveDaysMap) {
  return Object.values(map).some((active) => active);
}

const requirementSchema = object({
  condition: number().required(),
  maximumItems: number()
    .validEmptyString()
    .when(['condition', 'totalItemCondition'], {
      is: (condition, totalItemCondition) =>
        condition === ThresholdType.NumberOfItems && totalItemCondition === TotalItemConditionType.Between,
      then: (schema) => schema.required().moreThan(ref('minimumItems')),
    }),
  minimumGrams: number()
    .validEmptyString()
    .when('condition', {
      is: ThresholdType.ProductWeight,
      then: (schema) => schema.required().min(1),
    }),
  minimumItems: number()
    .validEmptyString()
    .when('condition', {
      is: ThresholdType.NumberOfItems,
      then: (schema) => schema.required().min(1),
    }),
  minimumSpend: number()
    .validEmptyString()
    .when('condition', {
      is: ThresholdType.SpendingAmount,
      then: (schema) => schema.required().min(0.01),
    }),
  totalItemCondition: number().required(),
});

export function isRequirementsStepValid(requirementsStepState: RequirementsStepState) {
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  return Object.values(requirements).every((requirement) => {
    try {
      requirementSchema.validateSync(requirement);
      return true;
    } catch {
      return false;
    }
  });
}

export function isRequirementConditionValid(requirementsStepState: RequirementsStepState) {
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  if (Object.keys(requirements).length > 1) {
    return !Object.values(requirements).some((requirement) => requirement.condition === ThresholdType.NoThreshold);
  }
  return true;
}

export const amountCalculationMethods: Array<CalculationMethods | 0> = [
  CalculationMethods.AmountOff,
  CalculationMethods.AmountOffTotal,
  CalculationMethods.PriceToAmount,
  CalculationMethods.PriceToAmountTotal,
];

export const percentCalculationMethods: Array<CalculationMethods | 0> = [
  CalculationMethods.PercentOff,
  CalculationMethods.PriceToCostPlusPercent,
];

const createRewardsStepSchema = (useDiscountsLoyaltyMultiplierFix: boolean) => {
  const baseSchema = {
    allowStacking: boolean(),
    amountOff: number().when('discountMethod', {
      is: (discountMethod) => amountCalculationMethods.includes(discountMethod),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.validEmptyString(),
    }),
    discountAppliesTo: number().required().min(1),
    discountMethod: number().required().min(1),
    ignoreNetTaxes: boolean(),
    manualApplyToMethod: number().when('isManualDiscount', {
      is: true,
      then: (schema) => schema.required().min(1),
    }),
    percentOff: number().when('discountMethod', {
      is: (discountMethod) => percentCalculationMethods.includes(discountMethod),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.validEmptyString(),
    }),
  };

  // Only add loyaltyMultiplier validation when the flag is enabled
  if (useDiscountsLoyaltyMultiplierFix) {
    return object({
      ...baseSchema,
      loyaltyMultiplier: number().when('discountMethod', {
        is: CalculationMethods.LoyaltyMultiplier,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.validEmptyString(),
      }),
    });
  }

  return object(baseSchema);
};

export function isRewardsStepValid(
  rewardsStepState: RewardsStepState & { isManualDiscount: boolean },
  useDiscountsLoyaltyMultiplierFix = false
) {
  try {
    const schema = createRewardsStepSchema(useDiscountsLoyaltyMultiplierFix);
    schema.validateSync(rewardsStepState);
    if (rewardsStepState.discountAppliesTo === DiscountAppliesTo.DifferentProducts) {
      requirementSchema.validateSync(rewardsStepState.condition);
    }
    return true;
  } catch {
    return false;
  }
}

const onlineDetailsStepSchema = object({
  onlineName: string().when('availableOnline', {
    is: true,
    then: (schema) => schema.required(),
  }),
  rank: number().when('availableOnline', {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.validEmptyString(),
  }),
});

export function isOnlineDetailsStepValid(
  onlineDetailsStepState: Pick<OnlineDetailsStepState, 'onlineName' | 'rank'> & { availableOnline: boolean }
) {
  try {
    onlineDetailsStepSchema.validateSync(onlineDetailsStepState);
    return true;
  } catch {
    return false;
  }
}

export function getRequirementsFromRequirementStepState(requirementsStepState: RequirementsStepState) {
  const { activeFilter: _, visited: __, ...requirements } = requirementsStepState;
  return requirements;
}

export function getMinItemsPlaceholderFromTotalItemCondition(condition: TotalItemConditionType) {
  if ([TotalItemConditionType.AtLeast, TotalItemConditionType.Between].includes(condition)) {
    return 'min';
  }
  return 'qty';
}

function formatDateTimeString(date: Date) {
  const dateString = format(date, 'MM/dd/yyyy');
  const timeString = format(date, 'p');

  return `${dateString} at ${timeString}`;
}

export function getStartDateText(start: Date | null) {
  if (!start) {
    return 'Not selected';
  }

  return formatDateTimeString(start);
}

export function getEndDateText(end: Date | null, specifyEndDate: boolean) {
  if (!specifyEndDate || !end) {
    return 'Never';
  }

  return formatDateTimeString(end);
}

export function getDayOfTheWeekText(map: ActiveDaysMap) {
  const activeDays = Object.entries(map)
    .filter(([_, active]) => active)
    .map(([day]) => day);
  if (activeDays.length === 0) {
    return 'No days selected';
  }
  if (activeDays.length === 7) {
    return 'Available every day';
  }
  return `Available ${activeDays.join(', ')}`;
}

export function getTimeRangeText(start: string, end: string) {
  if (start === '' && end === '') {
    return 'Available all day';
  }
  if (end === '') {
    return `Starting at ${start}`;
  }
  if (start === '') {
    return `Ending at ${end}`;
  }
  return `Between ${start} - ${end}`;
}

export function formatPercentValue(percentValue: number | '') {
  if (percentValue === 0 || percentValue === '') {
    return 0;
  }
  const decimalValue = percentValue / 100;
  return decimalValue;
}

export function formatTimeInput(input: string): string {
  const digits = input.replace(/\D/g, '');

  if (digits.length === 0) {
    return '';
  }
  if (digits.length === 1) {
    return digits;
  }
  if (digits.length === 2) {
    return digits;
  }
  if (digits.length === 3) {
    return `${digits.slice(0, 1)}:${digits.slice(1)}`;
  }
  if (digits.length >= 4) {
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  }

  return digits;
}

export function validateTime(timeString: string): string {
  if (!timeString || timeString.trim() === '') {
    return '';
  }

  if (!timeString.includes(':')) {
    return 'Time is invalid';
  }

  const timeRegex = /^([1-9]|1[0-2]):[0-5][0-9]$/;
  if (!timeRegex.test(timeString)) {
    return 'Time is invalid';
  }

  return '';
}

/**
 * Validate that end time is later than start time
 * @param startTime - Start time in format "H:MM AM" or "HH:MM PM"
 * @param endTime - End time in format "H:MM AM" or "HH:MM PM"
 * @param activeDays - Active days of the week map
 * @returns Error message or null if valid
 */
export function validateTimeRange(startTime: string, endTime: string, activeDays: ActiveDaysMap): string | null {
  // Only validate if both times exist and days are selected
  if (!startTime || !endTime || !isAnyDayOfWeekActive(activeDays)) {
    return null;
  }

  // Parse times in format "H:MM AM" or "HH:MM PM"
  const parseTime = (timeStr: string) => {
    const parts = timeStr.split(' ');
    if (parts.length !== 2) {
      return null;
    }

    const [time, meridiem] = parts;
    const [hoursStr, minsStr] = time.split(':');
    if (!hoursStr || !minsStr) {
      return null;
    }

    let hours = parseInt(hoursStr, 10);
    const mins = parseInt(minsStr, 10);

    // Convert to 24-hour format
    if (meridiem === 'PM' && hours !== 12) {
      hours += 12;
    } else if (meridiem === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + mins; // Return total minutes since midnight
  };

  const startMins = parseTime(startTime);
  const endMins = parseTime(endTime);

  if (startMins === null || endMins === null) {
    return null; // Skip validation if parsing failed
  }

  if (endMins <= startMins) {
    return 'End time must be later than the start time';
  }

  return null;
}
